import { useState } from "react";
import { Check } from "lucide-react";

const TIRE_POSITIONS = [
  { key: "front_left", label: "Bal első" },
  { key: "front_right", label: "Jobb első" },
  { key: "rear_left", label: "Bal hátsó" },
  { key: "rear_right", label: "Jobb hátsó" },
];

const TIRE_FIELDS = [
  { key: "type", label: "Típus" },
  { key: "dot", label: "DOT" },
  { key: "tread_depth", label: "Profilmélység (mm)" },
  { key: "condition", label: "Állapot" },
  { key: "rim_type", label: "Felnitípus" },
  { key: "brake_type", label: "Féktípus" },
  { key: "brake_pad", label: "Fékbetét vastagság" },
  { key: "brake_disc", label: "Féktárcsa állapot" },
];

function TireIcon({ condition }) {
  const isOk = condition && (condition.toLowerCase().includes("rendben") || condition.toLowerCase().includes("probleme"));
  const isBad = condition && (condition.toLowerCase().includes("csere") || condition.toLowerCase().includes("kopott"));
  const color = isOk ? "#22c55e" : isBad ? "#ef4444" : condition ? "#eab308" : "#555";
  return (
    <svg viewBox="0 0 64 64" className="w-12 h-12 sm:w-14 sm:h-14 mx-auto">
      <circle cx="32" cy="32" r="29" fill="none" stroke={color} strokeWidth="4" />
      <circle cx="32" cy="32" r="20" fill="none" stroke={color} strokeWidth="1.5" opacity="0.5" />
      <circle cx="32" cy="32" r="10" fill={color} opacity="0.15" />
      <circle cx="32" cy="32" r="4" fill={color} opacity="0.4" />
      {/* Tread lines */}
      <line x1="32" y1="3" x2="32" y2="10" stroke={color} strokeWidth="1.5" opacity="0.3" />
      <line x1="32" y1="54" x2="32" y2="61" stroke={color} strokeWidth="1.5" opacity="0.3" />
      <line x1="3" y1="32" x2="10" y2="32" stroke={color} strokeWidth="1.5" opacity="0.3" />
      <line x1="54" y1="32" x2="61" y2="32" stroke={color} strokeWidth="1.5" opacity="0.3" />
    </svg>
  );
}

export function TireInspection({ tireInspection = {} }) {
  const primary = tireInspection.primary || {};
  const secondary = tireInspection.secondary || {};

  const setHasData = (set) => TIRE_POSITIONS.some(p => {
    const t = set[p.key];
    return t && Object.values(t).some(v => v && v !== "");
  });

  const hasPrimary = setHasData(primary);
  const hasSecondary = setHasData(secondary);

  const [activeSet, setActiveSet] = useState(hasPrimary ? "primary" : "secondary");

  if (!hasPrimary && !hasSecondary) return null;

  const currentSet = activeSet === "secondary" && hasSecondary ? secondary : hasPrimary ? primary : secondary;

  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6" data-testid="tire-inspection-section">
      <h2 className="font-semibold mb-4 sm:mb-6 flex items-center gap-2">
        <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
          <svg className="w-4 h-4 text-[#D4AF37]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" />
          </svg>
        </div>
        Gumiabroncsok felmérése
      </h2>

      {/* Tabs */}
      {hasPrimary && hasSecondary && (
        <div className="flex gap-2 mb-5">
          <button
            onClick={() => setActiveSet("primary")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              activeSet === "primary"
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                : "border-white/10 text-white/50 hover:text-white hover:bg-white/5"
            }`}
            data-testid="tire-tab-primary"
          >
            {primary.label || "Elsődleges gumik"}
          </button>
          <button
            onClick={() => setActiveSet("secondary")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              activeSet === "secondary"
                ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                : "border-white/10 text-white/50 hover:text-white hover:bg-white/5"
            }`}
            data-testid="tire-tab-secondary"
          >
            {secondary.label || "Másodlagos gumik"}
          </button>
        </div>
      )}

      {/* Single set label when no tabs */}
      {!(hasPrimary && hasSecondary) && currentSet.label && (
        <p className="text-xs text-white/40 mb-4">{currentSet.label}</p>
      )}

      {/* Tire icons row */}
      <div className="grid grid-cols-4 gap-2 sm:gap-4 mb-5">
        {TIRE_POSITIONS.map((pos) => {
          const tire = currentSet[pos.key] || {};
          return (
            <div key={pos.key} className="text-center">
              <TireIcon condition={tire.condition} />
              <p className="text-[11px] sm:text-xs text-white/60 mt-1.5 font-medium">{pos.label}</p>
              {tire.tread_depth && (
                <p className="text-[10px] text-white/30 mt-0.5">{tire.tread_depth} mm</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Data table */}
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="w-full min-w-[480px] text-xs" data-testid="tire-data-table">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-2 px-3 text-white/40 font-medium w-[130px]"></th>
              {TIRE_POSITIONS.map((pos) => (
                <th key={pos.key} className="text-left py-2 px-2 text-white/60 font-medium">{pos.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TIRE_FIELDS.map((field) => {
              const hasAny = TIRE_POSITIONS.some(p => (currentSet[p.key] || {})[field.key]);
              if (!hasAny) return null;
              return (
                <tr key={field.key} className="border-b border-white/[0.04]">
                  <td className="py-2.5 px-3 text-white/40 whitespace-nowrap">{field.label}</td>
                  {TIRE_POSITIONS.map((pos) => {
                    const val = (currentSet[pos.key] || {})[field.key] || "-";
                    const isCondition = field.key === "condition";
                    const isOk = isCondition && val && (val.toLowerCase().includes("rendben") || val.toLowerCase().includes("probleme"));
                    return (
                      <td key={pos.key} className={`py-2.5 px-2 ${isCondition && isOk ? "text-green-400" : "text-white/70"}`}>
                        <div className="flex items-center gap-1">
                          {isCondition && isOk && <Check className="w-3 h-3 flex-shrink-0" />}
                          <span>{val}</span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export { TIRE_POSITIONS, TIRE_FIELDS };
